import { useAuth } from 'contexts/auth';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const PING_INTERVAL = 10 * 1000;
const RECONNECT_DELAY = 5000;

const WebSocketContext = createContext();
export const useWebsocket = () => useContext(WebSocketContext);

const WebsocketProvider = ({ children }) => {
    const { auth } = useAuth();

    const ws = useRef(null);
    const payloadProviders = useRef(new Map());
    const messageHandlers = useRef([]);

    const [isConnected, setIsConnected] = useState(false);

    const connect = useCallback(() => {
        if (!auth?.owner_account_id || ws.current) return;

        ws.current = new WebSocket(
            `${process.env.REACT_APP_WEBSOCKET_URL}?token=${encodeURIComponent(localStorage.getItem('auth_token'))}`
        );

        ws.current.onopen = () => {
            console.log('WebSocket connected.');
            setIsConnected(true);
        };

        ws.current.onclose = () => {
            console.log('WebSocket disconnected.');
            setIsConnected(false);
            ws.current = null;
        };

        ws.current.onmessage = (event) => {
            const data = JSON.parse(event.data);
            messageHandlers.current.forEach((handler) => handler(data));
        };

        ws.current.onerror = (error) => {
            console.error('WebSocket error', error);
        };
    }, [auth?.owner_account_id]);

    const disconnect = useCallback(() => {
        if (ws.current) {
            ws.current.close();
            ws.current = null;
        }
        setIsConnected(false);
    }, []);

    const sendMessage = useCallback((message) => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(
                JSON.stringify({
                    auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
                    message,
                })
            );
            return true;
        }

        console.warn('WebSocket is not open.');
        return false;
    }, []);

    const setPingPayload = useCallback((fn) => {
        const providerId = Symbol('pingPayloadProvider');
        payloadProviders.current.set(providerId, fn);

        return () => {
            payloadProviders.current.delete(providerId);
        };
    }, []);

    const onMessage = useCallback((handler) => {
        messageHandlers.current.push(handler);
        return () => {
            messageHandlers.current = messageHandlers.current.filter((h) => h !== handler);
        };
    }, []);

    const ping = useCallback(() => {
        if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
        const payload = [...payloadProviders.current.values()].reduce((accumulator, getPayload) => {
            const nextPayload = getPayload?.() || {};

            return Object.entries(nextPayload).reduce((result, [key, value]) => {
                if (Array.isArray(result[key]) && Array.isArray(value)) {
                    return {
                        ...result,
                        [key]: [...result[key], ...value],
                    };
                }

                return {
                    ...result,
                    [key]: value,
                };
            }, accumulator);
        }, {});

        sendMessage({ type: 'ping', ...payload });
    }, [sendMessage]);

    useEffect(() => {
        connect();
        return () => disconnect();
    }, [connect, disconnect]);

    useEffect(() => {
        if (!isConnected) return;

        const timeoutId = setTimeout(function pingLoop() {
            ping();
            setTimeout(pingLoop, PING_INTERVAL);
        }, PING_INTERVAL);

        return () => clearTimeout(timeoutId);
    }, [isConnected, ping]);

    useEffect(() => {
        if (!isConnected && auth?.owner_account_id) {
            setTimeout(connect, RECONNECT_DELAY);
        }
    }, [auth?.owner_account_id, connect, isConnected]);

    return (
        <WebSocketContext.Provider
            value={{
                isConnected,
                setPingPayload,
                onMessage,
                sendMessage,
            }}
        >
            {children}
        </WebSocketContext.Provider>
    );
};

export default WebsocketProvider;
